/**
 * useAssignments Hook
 * 
 * Custom hook for fetching and managing jabatan assignments.
 */

import * as React from "react";
import { managementService, type UserJabatanAssignment, type JabatanLevel } from "@/services/managementService";

export interface AssignmentFilters {
  user?: string;
  jabatan?: string;
  province?: string;
  city?: string;
  level?: JabatanLevel;
  isActive?: boolean;
}

export function useAssignments(filters?: AssignmentFilters) {
  const [assignments, setAssignments] = React.useState<UserJabatanAssignment[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [total, setTotal] = React.useState(0);

  const fetchAssignments = React.useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await managementService.getAllAssignments(filters);
      setAssignments(result.data);
      setTotal(result.total);
    } catch (err) {
      console.error("Error fetching assignments:", err);
      setError(err instanceof Error ? err.message : "Gagal memuat data penugasan");
      setAssignments([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  React.useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  return {
    assignments,
    loading,
    error,
    total,
    fetchAssignments,
  };
}

export function useCreateAssignment() {
  const [creating, setCreating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const createAssignment = React.useCallback(async (input: {
    user: string;
    jabatan: string;
    province?: string;
    city?: string;
    startDate?: string;
    endDate?: string;
    isActive?: boolean;
    notes?: string;
  }) => {
    setCreating(true);
    setError(null);

    try {
      const assignment = await managementService.createAssignment(input);
      return assignment;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Gagal membuat penugasan";
      setError(errorMessage);
      throw err;
    } finally {
      setCreating(false);
    }
  }, []);

  return {
    creating,
    error,
    createAssignment,
  };
}

export function useUpdateAssignment() {
  const [updating, setUpdating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const updateAssignment = React.useCallback(async (
    id: string,
    input: {
      province?: string;
      city?: string;
      startDate?: string;
      endDate?: string;
      isActive?: boolean;
      notes?: string;
    }
  ) => {
    setUpdating(true);
    setError(null);

    try {
      const assignment = await managementService.updateAssignment(id, input);
      return assignment;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Gagal mengupdate penugasan";
      setError(errorMessage);
      throw err;
    } finally {
      setUpdating(false);
    }
  }, []);

  return {
    updating,
    error,
    updateAssignment,
  };
}

export function useDeleteAssignment() {
  const [deleting, setDeleting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const deleteAssignment = React.useCallback(async (id: string) => {
    setDeleting(true);
    setError(null);

    try {
      await managementService.deleteAssignment(id);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Gagal menghapus penugasan";
      setError(errorMessage);
      throw err;
    } finally {
      setDeleting(false);
    }
  }, []);

  return {
    deleting,
    error,
    deleteAssignment,
  };
}

